import React, { useCallback, useEffect, useState } from "react";
import { humanizeDate } from "../../helpers/DateHelper";
import { capitalizeFirstLetter } from "../../helpers/TextHelpers";
import linkifyHtml from "linkify-html";
import "../../css/journal-shared-tribe-post.css";
import {
  useGetPostComments,
  useGetPostDetails,
  useToggleLikePost,
  useToggleSavePost,
} from "../../Controllers/Tribe/TribePostController";
import { toast } from "react-toastify";
import ActionButtonSection from "../Tribe/ActionButtonSection";
import { ReactionSummary } from "../Tribe/ReactionSummary";
import useReactions from "../../hooks/useReactions";
// import { emitTribePostUpdated, onTribePostUpdated } from "../../Utils/eventBus";
import StaticMessageCard from "../Tribe/CommentPostedCard";
import CommentInput from "../Tribe/CommentImput";
import { api } from "../../actions/api";
import useUserStore from "../../zustand-stores/UserStore";
import useTribePostStore from "../../zustand-stores/TribePostStore";
import { useQueryClient } from "@tanstack/react-query";
import parse from "html-react-parser";
import { parseLinkifiedHtmlWithVideoEmbeds } from "../../Utils/parseLinkifiedHtmlWithVideoEmbeds";
// import { useAuth } from "../../contexts/AuthContext";

const JournalSharedTribeSection = ({ tribePost }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    data: postDetails,
    isLoading: loadingPostDetails,
    refetch,
  } = useGetPostDetails(tribePost?.id);

  const [liked, setLiked] = useState(!!postDetails?.liked_by_current_user);
  const [likeCount, setLikeCount] = useState(
    Number(postDetails?.likes_count || 0),
  );
  const [isBookmarked, setIsBookmarked] = useState(
    !!postDetails?.is_bookmarked,
  );
  const [commentsCount, setCommentsCount] = useState(
    Number(postDetails?.comments_count || 0),
  );
  const [showComments, setShowComments] = useState(false);
  const [postCommentsData, setPostCommentsData] = useState({
    comments: [],
    meta: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingComment, setLoadingComment] = useState(false);
  const [postReactions, setPostReactions] = useState(null);
  const { reactions: availableReactions, isReady: reactionsReady } =
    useReactions();

  const { user: userProfile } = useUserStore();

  const queryClient = useQueryClient();

  const { mutate: toggleLikePost } = useToggleLikePost();
  const { mutate: toggleSavePost } = useToggleSavePost();
  const { mutate: getPostComments, isPending: loadingComments } =
    useGetPostComments();

  const postOwner = postDetails?.created_by || {};
  const emotionName = postDetails?.shared_journal_emotion_name ?? "Journal";
  const sharedJournalKeywords = postDetails?.shared_journal_keywords ?? [];

  const { setRefetchPost } = useTribePostStore();

  useEffect(() => {
    // Sync state when postDetails is fetched or updated
    if (postDetails) {
      setIsBookmarked(!!postDetails.is_bookmarked);
      setCommentsCount(Number(postDetails.comments_count || 0));
      if (postDetails.reactions) {
        setPostReactions(postDetails.reactions);
        setLiked(!!postDetails.reactions.current_user_reaction);
        const tc = postDetails.reactions.total_count;
        setLikeCount(
          tc !== undefined && tc !== null
            ? Number(tc)
            : Number(postDetails.likes_count || 0),
        );
      } else {
        setLiked(!!postDetails.liked_by_current_user);
        setLikeCount(Number(postDetails.likes_count || 0));
      }
    }
  }, [postDetails]);

  const handleReactionUpdate = useCallback((reactionsData) => {
    if (reactionsData) {
      setPostReactions({ ...reactionsData });
      setLiked(reactionsData.current_user_reaction !== null);
      setLikeCount(reactionsData.total_count || 0);
    }
  }, []);

  const formatLikes = (num) => {
    if (!num) return 0;
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  const toggleLike = () => {
    const prevLiked = liked;
    const prevCount = likeCount;
    // optimistic UI
    setLiked(!prevLiked);
    setLikeCount(prevLiked ? prevCount - 1 : prevCount + 1);
    toggleLikePost(postDetails.id, {
      onSuccess: (data) => {
        // keep detail cache fresh and signal list refetch
        // queryClient.invalidateQueries({ queryKey: ['tribePostDetails', postDetails.id] });
        // setRefetchPost();
        refetch();
      },
      onError: (error) => {
        // rollback
        setLiked(prevLiked);
        setLikeCount(prevCount);
        console.error("Error toggling like:", error);
        toast.error("Failed to toggle like. Please try again.");
      },
    });
  };

  const toggleBookmark = () => {
    const prev = isBookmarked;
    setIsBookmarked(!prev);
    toggleSavePost(postDetails.id, {
      onSuccess: () => {
        // keep detail cache fresh and signal list refetch
        queryClient.invalidateQueries({
          queryKey: ["tribePostDetails", postDetails.id],
        });
        // setRefetchPost();
      },
      onError: (error) => {
        setIsBookmarked(prev);
        console.error("Error toggling bookmark:", error);
        toast.error("Failed to toggle bookmark. Please try again.");
      },
    });
  };

  const handleCommentPressed = async () => {
    try {
      setIsLoading(true);
      const res = await api.get(`user/post/comments`, {
        params: { post_id: postDetails.id, per_page: 10, page: 1 },
      });

      setPostCommentsData({
        comments: res.data.data.comments,
        meta: res.data.data.meta,
      });
      setShowComments((prev) => !prev);
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Failed to fetch comments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreComments = async () => {
    if (
      !loadingComment &&
      postCommentsData.meta?.current_page &&
      postCommentsData.meta?.last_page &&
      postCommentsData.meta.current_page < postCommentsData.meta.last_page
    ) {
      try {
        setLoadingComment(true);
        const nextPage = postCommentsData.meta.current_page + 1;
        const params = {
          post_id: postDetails.id,
          per_page: 10,
          page: nextPage,
        };

        const res = await api.get(`user/post/comments`, { params });

        setPostCommentsData((prev) => ({
          ...prev,
          comments: [...prev.comments, ...res.data.data.comments],
          meta: res.data.data.meta,
        }));
      } catch (error) {
        console.error("Error fetching comments:", error);
        toast.error("Failed to fetch comments. Please try again.");
      } finally {
        setLoadingComment(false);
      }
    }
  };

  const linkConvert = (content) => {
    return linkifyHtml(content, {
      target: "_blank",
      rel: "noopener noreferrer",
    });
  };

  const invalidatePostDetail = () => {
    queryClient.invalidateQueries({
      queryKey: ["tribePostDetails", postDetails.id],
    });
  };

  const handleCommentPosted = async () => {
    // Refetch the latest comments after a comment is posted
    try {
      const res = await api.get(`user/post/comments`, {
        params: { post_id: postDetails.id, per_page: 10, page: 1 },
      });

      setPostCommentsData({
        comments: res.data.data.comments,
        meta: res.data.data.meta,
      });

      // Update comments count
      setCommentsCount((prev) => prev + 1);

      // Invalidate post details to keep everything in sync
      invalidatePostDetail();
    } catch (error) {
      console.error("Error fetching latest comments:", error);
      toast.error("Failed to fetch latest comments. Please try again.");
    }
  };

  if (loadingPostDetails || !postDetails) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="chat-section"
      style={{
        flex: 1,
        backgroundColor: "#121E29",
        borderRadius: "24px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        overflowY: "auto",
      }}
    >
      {/* Chat Header */}
      <div
        className="chat-header"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px 20px",
          borderBottom: "1px solid #333",
        }}
      >
        <img
          src={postOwner?.image || "/icons/james.svg"}
          alt="Profile"
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <div style={{ marginLeft: "12px" }}>
          <div
            style={{ color: "#e6ecf1", fontWeight: "500", fontSize: "16px" }}
          >
            {postOwner?.full_name || "Journal Chat"}
          </div>
          <div style={{ color: "#99D1FF", fontSize: "12px" }}>
            {humanizeDate(postDetails?.created_at)}
          </div>
        </div>
      </div>

      {/* Post Container */}
      <div
        className="journal-shared-tribe-post-container"
        style={{
          padding: "24px",
          borderBottom: "1px solid #333",
        }}
      >
        <div>
          <div
            className="post-text post-text-content d-flex flex-column gap-0"
            style={{
              color: "#C8CED3",
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              marginBottom: "10px",
            }}
          >
            {parseLinkifiedHtmlWithVideoEmbeds(
              expanded
                ? linkConvert(postDetails.text)
                : postDetails.text.length > 150
                  ? linkConvert(postDetails.text.slice(0, 150) + "...")
                  : linkConvert(postDetails.text),
            )}
          </div>

          <div className="emotion-name-text">
            User shared their {emotionName} reframe. Click below to view detail.
          </div>
          {postDetails.text.length > 150 && (
            <div
              className="post-text"
              style={{ marginTop: "8px", cursor: "pointer" }}
              onClick={() => setExpanded(!expanded)}
            >
              <svg
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_57395_31842)">
                  <path
                    d={
                      expanded
                        ? "M12.6663 7.35547H3.33301V8.6888H12.6663V7.35547Z"
                        : "M12.6663 8.6888H8.66634V12.6888H7.33301V8.6888H3.33301V7.35547H7.33301V3.35547H8.66634V7.35547H12.6663V8.6888Z"
                    }
                    fill="#008CFF"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_57395_31842">
                    <rect
                      width="16"
                      height="16"
                      fill="white"
                      transform="translate(0 0.0214844)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <span
                style={{
                  fontSize: "16px",
                  color: "#008CFF",
                  marginLeft: "12px",
                }}
              >
                {expanded ? "See Less" : "See More"}
              </span>
            </div>
          )}
        </div>

        <div className="tribe-journal-shared-post-box">
          <div
            style={
              {
                // display: "flex",
                // flexDirection: "column"
              }
            }
          >
            <div className="tribe-journal-shared-post-view-detail">
              {parse(tribePost?.title_answer || "No Title Provided")}
            </div>
          </div>

          <div>
            <img
              alt="arrow-icon"
              src="/icons/arrow-square-right.svg"
              style={{ width: "18px", height: "18px" }}
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          padding: "12px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <ReactionSummary
          reactionsData={postReactions}
          availableReactions={availableReactions}
        />
        <ActionButtonSection
          liked={liked}
          likeCount={likeCount}
          commentsCount={commentsCount}
          isBookmarked={isBookmarked}
          toggleLike={toggleLike}
          handleCommentPressed={handleCommentPressed}
          toggleBookmark={toggleBookmark}
          formatLikes={formatLikes}
          postId={postDetails.id}
          reactionsData={postReactions}
          reactionsReady={reactionsReady}
          availableReactions={availableReactions}
          onReactionUpdate={handleReactionUpdate}
        />
      </div>

      {/* Comments section */}
      {showComments && (
        <div style={{ padding: "0 20px 20px" }}>
          <hr
            className="my-2 my-sm-3"
            style={{
              borderTop: "1px solid var(--Bg-Border, #2A4052)",
              opacity: 1,
              width: "calc(100% + 40px)",
              marginLeft: "-20px",
              marginRight: "-20px",
            }}
          />
          <div className="d-flex flex-column gap-2">
            {postCommentsData.comments.length > 0 ? (
              <>
                {postCommentsData.comments.map((comment) => (
                  <StaticMessageCard
                    key={comment.id}
                    userProfile={userProfile}
                    comment={comment}
                    refreshMainComment={invalidatePostDetail}
                  />
                ))}
                {postCommentsData.meta.current_page <
                postCommentsData.meta?.last_page ? (
                  <p
                    className="mb-0"
                    style={{ color: "rgb(0, 140, 255)", cursor: "pointer" }}
                    onClick={() => loadMoreComments()}
                  >
                    View more comments...
                  </p>
                ) : null}
              </>
            ) : (
              <p className=" text-center " style={{ color: "#D0D8DF" }}>
                No comments yet
              </p>
            )}
          </div>

          {/* Comment input */}
          <hr
            className="my-2 my-sm-3"
            style={{
              borderTop: "1px solid var(--Bg-Border, #2A4052)",
              opacity: 1,
              width: "calc(100% + 40px)",
              marginLeft: "-20px",
              marginRight: "-20px",
            }}
          />
          <CommentInput
            postId={postDetails.id}
            userProfile={userProfile}
            onCommentPosted={handleCommentPosted}
          />
        </div>
      )}
    </div>
  );
};

export default JournalSharedTribeSection;
